/**
 * <park-steps> + <park-step> — the "Näin se toimii / How it works" numbered
 * step grid lifted from components/HomePage.astro (epic #138, issue #144; see
 * docs/design-system-rebuild-plan.md → "Isomorphic contract").
 *
 * Single source of truth = the pure `renderParkSteps()` function below. It is
 * consumed by BOTH render contexts:
 *   - the live Astro site, via the SSR wrapper `components/ds/ParkSteps.astro`,
 *     which calls it server-side and emits the markup inside <park-steps data-ssr>
 *     so the steps are in the initial HTML (SEO / no-JS), and
 *   - the Claude-Design preview, where <park-steps> parses its author-syntax
 *     <park-step num title> children (textContent = body) and renders client-side.
 * Because both call the SAME function, server and client markup are identical.
 *
 * The pure fn emits the whole `<ol class="step-grid">…</ol>` (no wrapper nodes,
 * so the `ol > li` relationship is never broken). Styling lives in
 * wc/css/park-steps.css (lifted 1:1 from HomePage.astro) — no <style> here.
 *
 * Author syntax:
 *   <park-steps>
 *     <park-step num="1" title="Pick a time">Choose a free slot…</park-step>
 *     …
 *   </park-steps>
 *
 * `<park-step>` is author-syntax only — it carries no own rendering, but is still
 * defined as a passthrough element so the tag is known to the platform.
 *
 * @dsSpec design-system#FR-3 — the recurring numbered how-it-works grid.
 *   (Traceability: specs/design-system/traceability.md; the generated card
 *   carries this same ref in its @dsCard marker — anton/forrest#846.)
 */

import { ParkElement, defineElement, esc, html } from "./base";

const TAG = "park-steps";
const STEP_TAG = "park-step";

export interface ParkStepData {
	num?: string;
	title?: string;
	body?: string;
}

/**
 * Pure render: (steps) → inner HTML string. No DOM access, so it is safe to call
 * server-side (the .astro wrapper) and client-side (the element). Emits the whole
 * `<ol class="step-grid">…</ol>`. A missing `num` falls back to the 1-based index.
 */
export function renderParkSteps(steps: ParkStepData[]): string {
	const items = steps
		.map((s, i) => {
			const num = s.num != null && s.num !== "" ? s.num : String(i + 1);
			return html`<li class="step-card"><span class="step-num">${esc(num)}</span><h3>${esc(s.title)}</h3><p>${esc(s.body)}</p></li>`;
		})
		.join("");
	return html`<ol class="step-grid">${items}</ol>`;
}

class ParkSteps extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}

	protected render(): string {
		// Parse the author-syntax <park-step num title> children (textContent = body)
		// captured in slotHtml into the step array, then render via the pure fn.
		const steps: ParkStepData[] = [];
		const tmp = document.createElement("div");
		tmp.innerHTML = this.slotHtml;
		for (const el of tmp.querySelectorAll(STEP_TAG)) {
			steps.push({
				num: el.getAttribute("num") ?? undefined,
				title: el.getAttribute("title") ?? undefined,
				body: (el.textContent ?? "").trim(),
			});
		}
		return renderParkSteps(steps);
	}
}

// Author-syntax only: parsed by <park-steps>. Extends ParkElement (not HTMLElement
// directly) so the module stays SSR-import-safe via base.ts's guarded ElementBase
// — a direct `extends HTMLElement` crashes the ds/*.astro wrappers in Node.
class ParkStep extends ParkElement {
	static get observedAttributes(): string[] {
		return [];
	}
	protected render(): string {
		return this.slotHtml;
	}
}

defineElement(TAG, ParkSteps);
defineElement(STEP_TAG, ParkStep);

export { ParkSteps, ParkStep };
